import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const stats = [
  { value: '3,000+', label: 'Homes Serviced' },
  { value: '500+', label: '5-Star Reviews' },
  { value: '15+', label: 'Years in Jacksonville' },
  { value: '100%', label: 'Satisfaction Guaranteed' },
];

export default function HeroSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount((c) => (c < 3 ? c + 1 : 0));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const badges = [
    'CSIA Certified',
    'NADCA Member',
    'Fully Insured',
    '5-Star Rated',
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background — high quality chimney/fireplace imagery */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/38e3c8649d9c7c12e6036ccdb681a080.jpg"
          alt="Professional chimney cleaning service Jacksonville FL — BBM Services"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b09]/92 via-[#0d0b09]/75 to-[#0d0b09]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b09]/60 via-transparent to-transparent" />
      </div>

      {/* Floating social proof card — top right */}
      <div className="absolute top-32 right-8 lg:right-16 hidden xl:block">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 max-w-[240px]">
          <div className="flex gap-0.5 mb-2">
            {[1,2,3,4,5].map((s) => (
              <div key={s} className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-brand-amber text-sm" />
              </div>
            ))}
          </div>
          <p className="text-white text-xs leading-relaxed mb-3">
            "BBM did our chimney and dryer vent same day. Absolutely professional — our home feels safer already."
          </p>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-brand-amber rounded-full flex-shrink-0">
              <span className="text-white text-xs font-bold">MR</span>
            </div>
            <div>
              <div className="text-white text-xs font-semibold">Maria R.</div>
              <div className="text-white/50 text-xs">Ponte Vedra, FL</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <div
          ref={ref}
          className={`max-w-3xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 bg-brand-amber/20 border border-brand-amber/40 text-amber-300 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
            <div className="w-1.5 h-1.5 bg-brand-amber rounded-full animate-pulse" />
            Certified & Insured — Serving Jacksonville Since 2010
          </div>

          {/* Headline */}
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-6">
            Cleaner Air.<br />
            <span className="text-brand-amber">Safer Home.</span><br />
            <span className="text-white/90">Real Peace of Mind.</span>
          </h1>

          <p className="text-white/75 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
            Jacksonville's most trusted specialists for{' '}
            <strong className="text-white font-semibold">chimney cleaning</strong>,{' '}
            <strong className="text-white font-semibold">air duct cleaning</strong>, and{' '}
            <strong className="text-white font-semibold">dryer vent cleaning</strong>.
            Certified professionals protecting your family's health and safety.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 px-9 rounded-xl text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-check-fill" />
              </div>
              Get a Free Quote
            </Link>
            <a
              href="tel:+19045550191"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/18 backdrop-blur-sm border border-white/25 hover:border-white/50 text-white font-semibold py-4 px-8 rounded-xl text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-brand-amber" />
              </div>
              Call (904) 555-0191
            </a>
          </div>

          {/* Trust badges row */}
          <div className="flex flex-wrap gap-3">
            {badges.map((badge) => (
              <span
                key={badge}
                className="flex items-center gap-1.5 text-xs text-white/70 bg-white/8 border border-white/15 px-3 py-1.5 rounded-full"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-brand-amber" />
                </div>
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Stats strip — bottom overlay */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="bg-black/40 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0 lg:divide-x lg:divide-white/15">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col items-center lg:px-8">
                  <span className="font-serif text-2xl lg:text-3xl font-bold text-brand-amber leading-none">{stat.value}</span>
                  <span className="text-white/60 text-xs mt-1 text-center">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
